import { type ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import Button from 'components/Button/Button';
import EmptyList from 'components/EmptyList/EmptyList';
import Panel from 'components/Panel/Panel';
import { AppRoutes } from 'constants/AppRoutes';
import { BUTTON_VARIANT } from 'constants/interfaces';

import { WatchTowerShell } from './TargetedMarketingChrome';
import {
  scheduleForTopic,
  setTopicPaused,
  setTopicStatus,
  topicLastRunAt,
  topicLinkedCompanyCount,
  topicOpenFindingCount,
} from './topicWatchAgents';
import useWatchTowerMock from './useWatchTowerMock';
import WatchTowerCreateTopicDialog from './WatchTowerCreateTopicDialog';
import { formatAgentType, formatIsoDate } from './watchTowerSelectors';

import type { WatchTopic } from './types';

import styles from './scss/TargetedMarketing.module.scss';

function statusClass(topic: WatchTopic): string {
  if (topic.status === 'archived') {
    return styles.statusArchived;
  }
  if (topic.paused || topic.status === 'draft') {
    return styles.statusDraft;
  }
  return styles.statusActive;
}

function statusLabel(topic: WatchTopic, t: (key: string) => string): string {
  if (topic.status === 'archived') {
    return topic.status;
  }
  if (topic.paused) {
    return t('targetedMarketing.paused');
  }
  return topic.status;
}

function WatchTowerTopics(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state, update } = useWatchTowerMock();
  const [wizardOpen, setWizardOpen] = useState(false);

  const topics = useMemo(
    () => [...(state.watchTopics || [])].sort((left, right) => left.title.localeCompare(right.title)),
    [state.watchTopics],
  );

  const openTopic = (id: number) => {
    navigate(generatePath(AppRoutes.watchTowerTopic, { id: String(id) }));
  };

  const openRuns = (topicId: number) => {
    const schedule = scheduleForTopic(state, topicId);
    if (schedule) {
      navigate(`${AppRoutes.watchTowerRuns}?scheduleId=${schedule.id}&topicId=${topicId}`);
      return;
    }
    navigate(`${AppRoutes.watchTowerRuns}?topicId=${topicId}`);
  };

  const onPause = (topic: WatchTopic) => {
    update((prev) => setTopicPaused(prev, topic.id, !topic.paused));
    toast.success(topic.paused
      ? t('targetedMarketing.topicResumed')
      : t('targetedMarketing.topicPaused'));
  };

  const onArchive = (topic: WatchTopic) => {
    const nextStatus = topic.status === 'archived' ? 'active' : 'archived';
    update((prev) => setTopicStatus(prev, topic.id, nextStatus));
    toast.success(nextStatus === 'archived'
      ? t('targetedMarketing.topicArchived')
      : t('targetedMarketing.topicRestored'));
  };

  const matchLabel = (kinds: WatchTopic['matchKinds']) => kinds
    .map((kind) => t(`targetedMarketing.matchKind_${kind}`))
    .join(', ');

  return (
    <WatchTowerShell
      primary="topics"
      runsSubView={null}
      actions={(
        <Button
          title={t('targetedMarketing.newTopic')}
          startIcon={<PlusIcon />}
          onClick={() => setWizardOpen(true)}
        />
      )}
    >
      <Panel className={styles.panel}>
        <h2 className={styles.sectionTitle}>{t('targetedMarketing.topicsTitle')}</h2>
        {topics.length === 0 ? (
          <EmptyList text={t('targetedMarketing.emptyTopics')}>
            <p className={styles.muted}>{t('targetedMarketing.emptyTopicsCta')}</p>
            <Button
              title={t('targetedMarketing.newTopic')}
              startIcon={<PlusIcon />}
              onClick={() => setWizardOpen(true)}
            />
          </EmptyList>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('targetedMarketing.title')}</th>
                  <th>{t('targetedMarketing.agents')}</th>
                  <th>{t('targetedMarketing.matchKinds')}</th>
                  <th>{t('targetedMarketing.lastRun')}</th>
                  <th>{t('targetedMarketing.openFindings')}</th>
                  <th>{t('targetedMarketing.linkedCompanies')}</th>
                  <th>{t('targetedMarketing.topicStatus')}</th>
                  <th>{t('targetedMarketing.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {topics.map((topic) => (
                  <tr key={topic.id}>
                    <td>
                      <button
                        type="button"
                        className={styles.nameButton}
                        onClick={() => openTopic(topic.id)}
                      >
                        {topic.title}
                      </button>
                    </td>
                    <td>{formatAgentType(topic.agentType)}</td>
                    <td>{matchLabel(topic.matchKinds) || t('targetedMarketing.none')}</td>
                    <td>{formatIsoDate(topicLastRunAt(state, topic.id) || topic.lastRunAt)}</td>
                    <td>{topicOpenFindingCount(state, topic.id)}</td>
                    <td>{topicLinkedCompanyCount(state, topic.id)}</td>
                    <td>
                      <span className={`${styles.statusPill} ${statusClass(topic)}`}>
                        {statusLabel(topic, t)}
                      </span>
                    </td>
                    <td>
                      <div className={styles.rowActions}>
                        <Button
                          className={styles.rowAction}
                          title={t('targetedMarketing.openTopic')}
                          variant={BUTTON_VARIANT.secondary}
                          onClick={() => openTopic(topic.id)}
                        />
                        <Button
                          className={styles.rowAction}
                          title={t('targetedMarketing.openRuns')}
                          variant={BUTTON_VARIANT.secondary}
                          onClick={() => openRuns(topic.id)}
                        />
                        {topic.status !== 'archived' ? (
                          <Button
                            className={styles.rowAction}
                            title={topic.paused
                              ? t('targetedMarketing.resume')
                              : t('targetedMarketing.pause')}
                            variant={BUTTON_VARIANT.secondary}
                            onClick={() => onPause(topic)}
                          />
                        ) : null}
                        <Button
                          className={styles.rowAction}
                          title={topic.status === 'archived'
                            ? t('targetedMarketing.unarchive')
                            : t('targetedMarketing.archive')}
                          variant={BUTTON_VARIANT.secondary}
                          onClick={() => onArchive(topic)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <WatchTowerCreateTopicDialog
        open={wizardOpen}
        state={state}
        onClose={() => setWizardOpen(false)}
        onApply={update}
      />
    </WatchTowerShell>
  );
}

export default WatchTowerTopics;
